import React, { useRef, useState } from 'react';
import { AccountPageLayout } from 'pages/Account/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Button, CheckBox, Text } from '@clariness/clarikit-react';
import { themes } from '@clariness/clarikit-tokens';
import { ShouldRender } from 'components/ShouldRender';
import { MultipleInvestigatorNotificationUpdateDto } from 'common/api';
import { BoxLoader } from 'components/loaders';
import { formData } from './AccountNotification';
import { ChevronDownIcon, ChevronUpIcon } from '@clariness/clarikit-icons';

interface Props {
  notifications: MultipleInvestigatorNotificationUpdateDto;
  setNotifications: (value: MultipleInvestigatorNotificationUpdateDto) => void;
  formDataList: Array<formData>;
  isFetching: boolean;
  setIsSaveEnabled: (value: boolean) => void;
}

export const NewSiteAppointments: React.FC<Props> = ({
  formDataList,
  isFetching,
  notifications,
  setIsSaveEnabled,
  setNotifications,
}: Props) => {
  const { space } = themes.light;
  const intl = useIntl();
  const [isShowStudies, setIsShowStudies] = useState(true);
  const allCheckboxRef = useRef<HTMLInputElement>(null);

  const isPatientEmailChecked = (value: number): boolean => {
    return Boolean(
      notifications.investigatorNotificationUpdateDto.find(
        (investigator) => investigator.id === value
      )?.patientAppointmentEmail
    );
  };

  const isAllChecked = (): boolean => {
    return Boolean(
      !notifications.investigatorNotificationUpdateDto.find(
        (investigator) => !investigator.patientAppointmentEmail
      )
    );
  };

  const isPartiallyChecked = !isAllChecked()
    ? Boolean(
        notifications.investigatorNotificationUpdateDto.filter(
          (investigator) => investigator.patientAppointmentEmail
        ).length
      )
    : false;

  const heading = intl.formatMessage({
    id: 'is.account_notification.new_site_appointment',
    defaultMessage: 'New Site Appointment',
  });

  return (
    <AccountPageLayout>
      {isFetching ? (
        <BoxLoader />
      ) : (
        <Box display="flex" flexDirection="column" gap={space['space-16']}>
          <Box
            display="grid"
            gap={space['space-16']}
            alignItems="center"
            style={{
              gridTemplateColumns: '5fr 80fr 15fr',
              marginBottom: '10px',
            }}
          >
            <CheckBox
              ref={allCheckboxRef}
              value="allNotification"
              name="allNotification"
              label={heading}
              labelHidden
              checked={isPartiallyChecked ? 'indeterminate' : isAllChecked()}
              onChange={(checked) => {
                const updateData =
                  notifications.investigatorNotificationUpdateDto.map(
                    (investigator) => {
                      return {
                        ...investigator,
                        patientAppointmentEmail: checked,
                      };
                    }
                  );
                setNotifications({
                  investigatorNotificationUpdateDto: updateData,
                });
                setIsSaveEnabled(false);
              }}
              data-test-id="all-notifications-checkbox"
            />
            <Text
              as="h4"
              color={!isAllChecked() ? 'color-gray-variant-2' : undefined}
            >
              {heading}
            </Text>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="tertiary"
                color="color-gray-variant-1"
                iconLeft={isShowStudies ? ChevronUpIcon : ChevronDownIcon}
                accessibilityLabel={intl.formatMessage(
                  isShowStudies
                    ? {
                        id: 'is.account_notification.hide_studies',
                        defaultMessage: 'Hide studies',
                      }
                    : {
                        id: 'is.account_notification.show_studies',
                        defaultMessage: 'Show studies',
                      }
                )}
                ariaExpanded={isShowStudies}
                onClick={() => setIsShowStudies(!isShowStudies)}
                data-test-id="site-employee-back-button"
              />
            </Box>
            <Box />
            <Text
              as="p"
              color={!isAllChecked() ? 'color-gray-variant-2' : undefined}
            >
              <FormattedMessage
                id="is.account_notification.new_site_appointment_paragraph"
                defaultMessage="E-mail send to the patient on behalf of 
                site with appointment details and meeting follow up reminder"
              />
            </Text>
          </Box>

          <ShouldRender when={isShowStudies}>
            <Box display="flex" flexDirection="column" gap={space['space-16']}>
              {formDataList.map((formData) => {
                return (
                  <Box style={{ margin: '5px' }} key={formData.studyName}>
                    <Box
                      display="grid"
                      gap={space['space-16']}
                      style={{ gridTemplateColumns: '5fr 75fr 20fr' }}
                    >
                      <Box />
                      <Box style={{ marginBottom: '10px' }}>
                        <Text as="p" fontWeight="bold" breakWord>
                          {formData.studyName}
                        </Text>
                      </Box>
                      <Box />
                    </Box>
                    <Box
                      display="grid"
                      gap={space['space-16']}
                      style={{ gridTemplateColumns: '5fr 75fr 20fr' }}
                    >
                      <Box />
                      <Box />
                      <Box
                        display="flex"
                        gap={space['space-8']}
                        justifyContent="center"
                      />

                      {formData.value?.map((investigator) => (
                        <React.Fragment key={investigator.id}>
                          <Box />
                          <Box maxWidth="400px">
                            <Text
                              as="p"
                              breakWord
                              color={
                                !isPatientEmailChecked(investigator.id)
                                  ? 'color-gray-variant-2'
                                  : undefined
                              }
                            >
                              {investigator.studyName} (
                              {investigator.studyLanguage})
                            </Text>
                          </Box>
                          <Box
                            display="flex"
                            gap={space['space-32']}
                            justifyContent="center"
                          >
                            <CheckBox
                              value={String(investigator.id)}
                              name="instantNotification"
                              label={`${investigator.studyName} (${investigator.studyLanguage})`}
                              labelHidden
                              checked={isPatientEmailChecked(investigator.id)}
                              onChange={(checked) => {
                                const update =
                                  notifications.investigatorNotificationUpdateDto.map(
                                    (item) => {
                                      if (item.id === investigator.id) {
                                        return {
                                          ...item,
                                          patientAppointmentEmail: checked,
                                        };
                                      }
                                      return { ...item };
                                    }
                                  );
                                setNotifications({
                                  investigatorNotificationUpdateDto: update,
                                });
                                setIsSaveEnabled(false);
                              }}
                              data-test-id="instant-notification-checkbox"
                            />
                          </Box>
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </ShouldRender>
        </Box>
      )}
    </AccountPageLayout>
  );
};
